import React, {useContext} from "react";
import {useState, useRef, useEffect} from "react";
import {LanguageContext} from "./contexts/LanguageContext";
import {GenericHint} from "./components/GenericHint";

import Joyride, {ACTIONS, EVENTS, ORIGIN, STATUS, CallBackProps} from 'react-joyride';
import {useNavigate} from "react-router-dom";

const AUTO_ADVANCE_MS = 5500;

export const Onboarding: React.FC = () => {

    const navigate = useNavigate();
    const [animate, setAnimate] = useState(false);

    const autoAdvanceRef = useRef(false);
    const joyrideRef = useRef<any>(null);
    const [stepIndex, setStepIndex] = useState(0);
    const timerRef = useRef<number | null>(null);

    const clearTimer = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    };

    const startTimer = () => {
        clearTimer();
        timerRef.current = window.setTimeout(() => {
            // Don't increment stepIndex directly
            // Instead, trigger Joyride's next action programmatically
            if (stepIndex < steps.length - 1) {
                autoAdvanceRef.current = true;  // ✅ Flag this as auto-advance
                setStepIndex((i) => Math.min(i + 1, steps.length - 1));
            }
        }, AUTO_ADVANCE_MS);
    };

    const goNext = () => {
        clearTimer();
        setStepIndex((i) => Math.min(i + 1, steps.length - 1));
    };

    const goBack = () => {
        clearTimer();
        setStepIndex((i) => Math.max(i - 1, 0));
    };

    const handleJoyrideCallback = (data) => {
        const {action, index, origin, status, type} = data;

        if (action === ACTIONS.CLOSE) {
            clearTimer();
            navigate("/upload");
        }
        if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
            clearTimer();
            navigate("/upload");
        }

        if (action === ACTIONS.NEXT && index === 4) {
            clearTimer();
            navigate("/upload");
        }

        // ✅ Only handle manual button clicks
        if (type === EVENTS.STEP_AFTER && !autoAdvanceRef.current) {
            if (action === ACTIONS.NEXT) goNext();
            if (action === ACTIONS.PREV) goBack();
        }

        if (type === EVENTS.STEP_BEFORE) {
            autoAdvanceRef.current = false;  // ✅ Reset flag
            startTimer();
            setAnimate(false);
            requestAnimationFrame(() => setAnimate(true));
        }

    };

    const language = useContext(LanguageContext);
    const imgRulesA = new URL("../img/ID.manual-1.gif", import.meta.url);
    const imgRulesB = new URL("../img/ID.manual-2.gif", import.meta.url);
    const imgRulesC = new URL("../img/ID.manual-3.gif", import.meta.url);
    const imgRulesD = new URL("../img/ID.manual-4.gif", import.meta.url);
    const imgRulesE = new URL("../img/ID.manual-5.gif", import.meta.url);


    const steps: Step[] = [
        {
            target: "body",
            placement: "center",
            content: (
                <div className={animate ? "fade-in" : ""}>
                    <GenericHint
                        imgSrc={imgRulesA.toString()}
                        doText={language.hints_do_text}
                        dontText={language.hints_dont_text}
                    />
                </div>
            ),
        },
        {
            target: "body",
            placement: "center",
            content: (
                <div className={animate ? "fade-in" : ""}>
                    <GenericHint
                        imgSrc={imgRulesB.toString()}
                        doText={language.hints2_do_text}
                        dontText={language.hints2_dont_text}
                    />
                </div>
            ),
        },
        {
            target: "body",
            placement: "center",
            content: (
                <div className={animate ? "fade-in" : ""}>
                    <GenericHint
                        imgSrc={imgRulesC.toString()}
                        doText={language.hints3_do_text}
                        dontText={language.hints3_dont_text}
                    />
                </div>
            ),
        },
        {
            target: "body",
            placement: "center",
            content: (
                <div className={animate ? "fade-in" : ""}>
                    <GenericHint
                        imgSrc={imgRulesD.toString()}
                        doText={language.hints4_do_text}
                        dontText={language.hints4_dont_text}
                    />
                </div>
            ),
        },
        {
            target: "body",
            placement: "center",
            content: (
                <div className={animate ? "fade-in" : ""}>
                    <GenericHint
                        imgSrc={imgRulesE.toString()}
                        doText={language.hints5_do_text}
                        dontText={language.hints5_dont_text}
                    />
                </div>
            ),
        },
    ];

    // ✅ Stop autoplay at last step
    useEffect(() => {
        if (stepIndex >= steps.length) {
            clearTimer();
            // setRun(false);
        }
    }, [stepIndex, steps.length]);


    return (
        <div className="app">
            <Joyride
                ref={joyrideRef}
                steps={steps}
                stepIndex={stepIndex}
                continuous
                disableOverlayClose
                disableCloseOnEsc
                showSkipButton
                callback={handleJoyrideCallback}
                styles={{
                    options: {
                        arrowColor: 'rgba(0, 161, 255, 1)',
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                        overlayColor: 'rgba(255, 255, 255, 1)',
                        primaryColor: '#000',
                        textColor: '#004a14',
                        width: "80vw",
                        zIndex: 1000,
                    },
                }}
              locale={{
                last: language.hints_next_button,
              }}
            />
        </div>
    );
};
